import { useContext, useEffect, useMemo } from 'react';
import { LanguageContext } from '../../translation/src/context/LanguageContext';
import { Language } from '../../types/graphqlTypes';

/**
 * Viewer shape with translationService for the bridge hook.
 * Subset of the Potree Viewer — avoids direct Potree type imports.
 */
interface ViewerWithTranslation {
  translationService: {
    setLanguageData(language: string, translations: Record<string, string>): void;
  };
}

/**
 * Builds a source-string-keyed translation map by joining the English
 * translations (hash → source string) with the current language translations
 * (hash → translated string).
 */
function buildSourceMap(enTranslations: Record<string, string>, currentTranslations: Record<string, string>): Record<string, string> {
  const map: Record<string, string> = {};
  for (const hash of Object.keys(enTranslations)) {
    const sourceString = enTranslations[hash];
    map[sourceString] = currentTranslations[hash] || sourceString;
  }
  return map;
}

/**
 * Bridge hook that syncs the React translation system with Potree's TranslationService.
 *
 * Builds a { sourceString → translatedString } map from the LanguageContext
 * and pushes it to `viewer.translationService` whenever the language changes.
 *
 * @param viewer - The Potree viewer instance (may be undefined during init)
 */
export function usePotreeTranslation(viewer: ViewerWithTranslation | undefined): void {
  const { language, languages } = useContext(LanguageContext);

  const sourceMap = useMemo(() => {
    const enTranslations = languages[Language.En] || {};
    const currentTranslations = languages[language] || {};
    return buildSourceMap(enTranslations, currentTranslations);
  }, [language, languages]);

  useEffect(() => {
    if (!viewer?.translationService) return;
    viewer.translationService.setLanguageData(language.toLowerCase(), sourceMap);
  }, [viewer, language, sourceMap]);
}
